import {Injectable} from '@angular/core';
import {forkJoin, Observable, Subject} from 'rxjs';
import {map} from 'rxjs/operators';
import {AlertService} from './alert.service';
import {ReferralApiService} from './referral-api.service';

/*
 * Referral (refer & earn) program store.
 *
 * NOTE: There is no backend API for the referral program yet for the
 * partner/referral/payout/KYC lists below, so this service still generates
 * DEMO DATA in memory for those (same shape as the design prototype). Every
 * mutating method below is marked with the endpoint it should call once the
 * API exists — swap the in-memory update for a BaseService request there.
 *
 * The sidebar badge counts (counts()) are the exception: the real
 * admin/referral/* endpoints for them already exist, so they are fetched
 * live instead of being derived from the demo arrays.
 */

export type ReferralStatus = 'new' | 'counselling' | 'enrolled' | 'started' | 'rejected';
export type KycStatus = 'verified' | 'pending' | 'rejected' | 'none';
export type PayoutStatus = 'pending' | 'approved' | 'paid';
export type PartnerLevel = 'Gold' | 'Silver' | 'Bronze';

export interface RefProgram {
    id: number;
    name: string;
    uni: string;
    reward: number;
    discount: number;
    active: boolean;
}

export interface RefPartner {
    id: number;
    name: string;
    phone: string;
    code: string;
    city: string;
    joined: number;
    status: 'active' | 'blocked';
    kyc: KycStatus;
    pan: string;
    upi: string;
    bank: string;
    kycReason: string;
}

export interface RefLog {
    s: ReferralStatus;
    t: number;
    by: string;
    note?: string;
}

export interface Referral {
    id: number;
    name: string;
    phone: string;
    partnerId: number;
    programId: number;
    reward: number;
    counsellor: string;
    status: ReferralStatus;
    date: number;
    log: RefLog[];
}

export interface RefPayout {
    id: number;
    refId: number;
    partnerId: number;
    type: 'Advance' | 'Final';
    amount: number;
    status: PayoutStatus;
    date: number;
    txn: string;
}

export interface RefActivity {
    t: number;
    text: string;
}

export interface PartnerStats {
    refs: number;
    enr: number;
    earned: number;
    level: PartnerLevel;
}

export const DAY = 864e5;
// TODO: confirm TDS rate and threshold with your CA
export const TDS_RATE = 0.02;

export const REFERRAL_STATUS: { [k in ReferralStatus]: string } = {
    new: 'New',
    counselling: 'In counselling',
    enrolled: 'Enrolled (fee paid)',
    started: 'Program started',
    rejected: 'Not interested'
};
export const REFERRAL_STATUS_KEYS = Object.keys(REFERRAL_STATUS) as ReferralStatus[];

export const KYC_LABEL: { [k in KycStatus]: string } = {
    verified: 'Verified',
    pending: 'Pending',
    rejected: 'Rejected',
    none: 'Not submitted'
};

export const REF_COUNSELLORS = ['Neha Sharma', 'Rohit Mehra', 'Pooja Verma', 'Sandeep Yadav'];

@Injectable({
    providedIn: 'root'
})
export class ReferralService {
    readonly now = Date.now();
    programs: RefProgram[] = [];
    partners: RefPartner[] = [];
    referrals: Referral[] = [];
    payouts: RefPayout[] = [];
    activity: RefActivity[] = [];

    /** Emits after any change so badges and open views can refresh. */
    changed = new Subject<void>();

    private seed = 11;
    private payoutId = 0;

    constructor(private alertService: AlertService, private referralApi: ReferralApiService) {
        this.generateDemoData();
    }

    /* ---------------- Lookups ---------------- */

    partnerOf(r: Referral): RefPartner {
        return this.partners[r.partnerId];
    }

    programOf(r: Referral): RefProgram {
        return this.programs[r.programId];
    }

    reached(r: Referral, s: ReferralStatus): boolean {
        return r.log.some(l => l.s === s);
    }

    statsFor(p: RefPartner): PartnerStats {
        const rs = this.referrals.filter(r => r.partnerId === p.id);
        const enr = rs.filter(r => r.status === 'enrolled' || r.status === 'started').length;
        const earned = this.payouts.filter(x => x.partnerId === p.id && x.status === 'paid').reduce((a, x) => a + x.amount, 0);
        return {refs: rs.length, enr, earned, level: enr >= 10 ? 'Gold' : enr >= 4 ? 'Silver' : 'Bronze'};
    }

    /**
     * Live sidebar badge counts, sourced from the real admin/referral/* endpoints
     * (not the demo arrays above). "unassigned" uses the PENDING referral status
     * as the closest real equivalent — the backend has no counsellor-assignment
     * concept of its own.
     */
    counts(): Observable<{ unassigned: number, pendingPayouts: number, pendingKyc: number, pendingPayoutAccounts: number }> {
        return forkJoin({
            unassigned: this.referralApi.getReferrals({status: 'PENDING', per_page: 1})
                .pipe(map(res => res?.total ?? 0)),
            pendingPayouts: this.referralApi.getRedemptionsSummary()
                .pipe(map(res => res?.waiting_approval?.count ?? 0)),
            pendingKyc: this.referralApi.getKycRequests({status: 'SUBMITTED,UNDER_REVIEW', per_page: 1})
                .pipe(map(res => res?.total ?? 0)),
            pendingPayoutAccounts: this.referralApi.getPayoutAccounts({status: 'SUBMITTED,UNDER_REVIEW', per_page: 1})
                .pipe(map(res => res?.total ?? 0))
        });
    }

    /* ---------------- Mutations (swap for API calls) ---------------- */

    // TODO: PUT /admin/referral-partners/:id/status
    setPartnerBlocked(p: RefPartner, blocked: boolean, reason = '') {
        p.status = blocked ? 'blocked' : 'active';
        this.log(blocked ? `${p.name} blocked (${reason})` : `${p.name} unblocked`);
        this.toast(blocked ? 'Partner blocked' : 'Partner unblocked');
    }

    // TODO: PUT /admin/referrals/:id/counsellor
    setCounsellor(r: Referral, counsellor: string) {
        r.counsellor = counsellor;
        this.log(counsellor ? `${r.name} assigned to ${counsellor}` : `${r.name} unassigned`);
        this.toast(counsellor ? 'Counsellor assigned' : 'Counsellor removed');
    }

    // TODO: PUT /admin/referrals/:id/status (backend should create the payouts)
    changeStatus(r: Referral, to: ReferralStatus, note = '') {
        r.status = to;
        r.log.push({s: to, t: Date.now(), by: 'You', note});
        const has = (type: string) => this.payouts.some(p => p.refId === r.id && p.type === type);
        if ((to === 'enrolled' || to === 'started') && !has('Advance')) {
            this.addPayout(r, 'Advance', 'pending', Date.now());
        }
        if (to === 'started' && !has('Final')) {
            this.addPayout(r, 'Final', 'pending', Date.now());
        }
        if (to === 'rejected' || to === 'new' || to === 'counselling') {
            this.payouts = this.payouts.filter(p => !(p.refId === r.id && p.status === 'pending'));
        }
        this.log(`${r.name} moved to “${REFERRAL_STATUS[to]}”`);
        this.toast(to === 'enrolled' ? 'Advance payout added for approval' : to === 'started' ? 'Final payout added for approval' : 'Status updated');
    }

    // TODO: POST /admin/referral-payouts/approve
    approvePayouts(ids: number[]) {
        let total = 0;
        ids.forEach(id => {
            const p = this.payouts.find(x => x.id === id);
            if (p) {
                p.status = 'approved';
                total += p.amount;
            }
        });
        const s = ids.length > 1 ? 's' : '';
        this.log(`${ids.length} payout${s} approved (${this.inr(total)})`);
        this.toast(`${ids.length} payout${s} approved`);
    }

    // TODO: PUT /admin/referral-payouts/:id/paid
    markPaid(p: RefPayout, txn: string) {
        p.status = 'paid';
        p.txn = txn;
        p.date = Date.now();
        this.log(`${this.inr(p.amount)} paid to ${this.partners[p.partnerId].name}`);
        this.toast('Marked as paid');
    }

    // TODO: PUT /admin/referral-partners/:id/kyc
    setKyc(p: RefPartner, ok: boolean, reason = '') {
        p.kyc = ok ? 'verified' : 'rejected';
        p.kycReason = reason;
        this.log(`KYC ${ok ? 'approved' : 'rejected'} for ${p.name}`);
        this.toast(ok ? 'KYC approved' : 'KYC rejected');
    }

    // The real PUT (SettingService.update, referral_programs field) happens in
    // ReferralProgramsComponent.save() before this runs; this just keeps the in-memory demo
    // catalog (used by the rest of this mock referral admin) in sync with it.
    savePrograms(values: { id: number, reward: number, discount: number, active: boolean }[]) {
        values.forEach(v => Object.assign(this.programs[v.id], v));
        this.log('Program rewards updated');
        this.toast('New referrals use the updated rewards');
    }

    /* ---------------- Utils ---------------- */

    inr(n: number): string {
        return '₹' + Math.round(n).toLocaleString('en-IN');
    }

    csv(name: string, rows: any[][]) {
        const text = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([text], {type: 'text/csv'}));
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        this.toast('Downloaded ' + name);
    }

    toast(message: string) {
        this.alertService.showSuccess({title: 'Referral', message});
    }

    private log(text: string) {
        this.activity.unshift({t: Date.now(), text: text + ' by you'});
        this.changed.next();
    }

    private addPayout(rf: Referral, type: 'Advance' | 'Final', status: PayoutStatus, t: number) {
        this.payouts.push({
            id: this.payoutId++, refId: rf.id, partnerId: rf.partnerId, type, amount: rf.reward / 2, status, date: t,
            txn: status === 'paid' ? 'UTR' + Math.floor(1e9 + this.rnd() * 9e9) : ''
        });
    }

    /* ---------------- Demo data ---------------- */

    private rnd(): number {
        this.seed = (this.seed * 16807) % 2147483647;
        return (this.seed - 1) / 2147483646;
    }

    private pick<T>(a: T[]): T {
        return a[Math.floor(this.rnd() * a.length)];
    }

    private phone(): string {
        return String(this.pick([6, 7, 8, 9])) + String(Math.floor(this.rnd() * 1e9)).padStart(9, '0');
    }

    private generateDemoData() {
        const NOW = this.now;
        const CITIES = ['Delhi', 'Jaipur', 'Lucknow', 'Pune', 'Patna', 'Indore', 'Noida', 'Bhopal', 'Chandigarh', 'Kanpur'];
        this.programs = ([
            ['Online MBA', 'Amity University Online', 10000, 5000], ['Online MBA', 'Manipal University Jaipur', 9000, 4500],
            ['Executive MBA', 'NMIMS Global', 8000, 4000], ['Online MCA', 'Chandigarh University', 7000, 3500],
            ['Online BCA', 'LPU Online', 5000, 2500], ['Online BBA', 'Jain University', 5000, 2500],
            ['Online B.Com', 'DY Patil Online', 4000, 2000], ['Online M.Com', 'Amity University Online', 5000, 2500],
            ['Data Science certificate', 'IIT partner program', 6000, 3000]
        ] as [string, string, number, number][]).map((p, i) => ({id: i, name: p[0], uni: p[1], reward: p[2], discount: p[3], active: true}));

        const PN = ['Rahul Verma', 'Anjali Singh', 'Rohit Kumar', 'Mohit Vyas', 'Sneha Patel', 'Arjun Nair', 'Kavya Reddy', 'Imran Khan',
            'Pooja Joshi', 'Vikas Yadav', 'Neha Gupta', 'Deepak Singh', 'Meena Rao', 'Amit Tiwari', 'Priya Sharma', 'Sunil Mishra',
            'Ritu Agarwal', 'Faisal Ahmed', 'Kiran Das', 'Manoj Pandey', 'Swati Jain', 'Harsh Malhotra', 'Ayesha Siddiqui', 'Gaurav Chauhan'];
        const SN = ['Aman', 'Riya', 'Karan', 'Sana', 'Vivek', 'Nisha', 'Farhan', 'Tanvi', 'Rohan', 'Isha', 'Yash', 'Divya', 'Kunal',
            'Aditi', 'Nikhil', 'Shreya', 'Varun', 'Muskan', 'Akash', 'Pallavi'];
        const LN = ['Gupta', 'Mehta', 'Singh', 'Khan', 'Rao', 'Jain', 'Ali', 'Shah', 'Verma', 'Das', 'Kapoor', 'Bansal', 'Saxena', 'Joshi'];

        this.partners = PN.map((n, i) => {
            const k = this.rnd();
            const first = n.split(' ')[0];
            return {
                id: i, name: n, phone: this.phone(), code: first.toUpperCase() + (100 + Math.floor(this.rnd() * 900)),
                city: this.pick(CITIES), joined: NOW - Math.floor(this.rnd() * 300 + 20) * DAY,
                status: this.rnd() < .06 ? 'blocked' : 'active',
                kyc: k < .6 ? 'verified' : k < .78 ? 'pending' : k < .86 ? 'rejected' : 'none',
                pan: 'ABCDE' + (1000 + Math.floor(this.rnd() * 9000)) + 'F', upi: first.toLowerCase() + '@okaxis',
                bank: this.pick(['SBI', 'HDFC Bank', 'ICICI Bank', 'Axis Bank', 'PNB']), kycReason: ''
            } as RefPartner;
        });

        const weight = this.partners.map((_, i) => Math.max(1, 12 - i * .45));
        const totalWeight = weight.reduce((a, b) => a + b, 0);
        const pickPartner = () => {
            let r = this.rnd() * totalWeight;
            for (let i = 0; i < weight.length; i++) {
                r -= weight[i];
                if (r <= 0) {
                    return this.partners[i];
                }
            }
            return this.partners[0];
        };

        for (let i = 0; i < 160; i++) {
            const age = Math.floor(this.rnd() * 180);
            const date = NOW - age * DAY - Math.floor(this.rnd() * DAY);
            const r = this.rnd();
            let st: ReferralStatus;
            if (age < 6) {
                st = r < .6 ? 'new' : 'counselling';
            } else if (age < 25) {
                st = r < .25 ? 'new' : r < .6 ? 'counselling' : r < .85 ? 'enrolled' : 'rejected';
            } else {
                st = r < .08 ? 'new' : r < .25 ? 'counselling' : r < .45 ? 'enrolled' : r < .75 ? 'started' : 'rejected';
            }
            const p = pickPartner(), prog = this.pick(this.programs);
            const coun = st === 'new' && this.rnd() < .55 ? '' : this.pick(REF_COUNSELLORS);
            const rf: Referral = {
                id: i, name: this.pick(SN) + ' ' + this.pick(LN), phone: this.phone(), partnerId: p.id, programId: prog.id,
                reward: prog.reward, counsellor: coun, status: st, date, log: [{s: 'new', t: date, by: p.name}]
            };
            if (st !== 'new') {
                rf.log.push({s: 'counselling', t: date + 2 * DAY, by: coun || 'System'});
            }
            if (st === 'enrolled' || st === 'started') {
                rf.log.push({s: 'enrolled', t: date + 9 * DAY, by: coun});
            }
            if (st === 'started') {
                rf.log.push({s: 'started', t: date + 22 * DAY, by: coun});
            }
            if (st === 'rejected') {
                rf.log.push({s: 'rejected', t: date + 6 * DAY, by: coun});
            }
            this.referrals.push(rf);
        }
        this.referrals.sort((a, b) => b.date - a.date);

        this.referrals.forEach(rf => {
            const e = rf.log.find(l => l.s === 'enrolled'), s = rf.log.find(l => l.s === 'started');
            if (e) {
                this.addPayout(rf, 'Advance', NOW - e.t > 20 * DAY ? 'paid' : this.rnd() < .5 ? 'approved' : 'pending', e.t + 5 * DAY);
            }
            if (s) {
                this.addPayout(rf, 'Final', NOW - s.t > 20 * DAY ? 'paid' : this.rnd() < .4 ? 'approved' : 'pending', s.t + 5 * DAY);
            }
        });

        this.referrals.slice(0, 6).forEach(r => this.activity.push({
            t: r.date, text: `${this.partnerOf(r).name} referred ${r.name} for ${this.programOf(r).name}`
        }));
        this.payouts.filter(p => p.status === 'paid').sort((a, b) => b.date - a.date).slice(0, 3).forEach(p => this.activity.push({
            t: Math.min(p.date, NOW - DAY), text: `${this.inr(p.amount)} ${p.type.toLowerCase()} paid to ${this.partners[p.partnerId].name}`
        }));
        this.activity.sort((a, b) => b.t - a.t);
    }
}
